import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPhone, FiBookOpen, FiMonitor, FiFileText } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';

export default function StudentTeacherDetail() {
    const { teacherId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('classes');
    const [teacher, setTeacher] = useState(null);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const loadTeacherDetails = async () => {
            try {
                const data = await fetchAPI(`/student/get_teacher_details.php?teacher_id=${teacherId}`);
                setTeacher(data);
            } catch (err) {
                console.error("Failed to load teacher details", err);
            } finally {
                setLoading(false);
            }
        };
        loadTeacherDetails();
    }, [teacherId]);

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading teacher details...</div>;
    if (!teacher) return <div className="p-8 text-center text-red-500 font-bold">Teacher not found</div>;

    const { classes, study_packs } = teacher;

    return (
        <div className="space-y-6">
            {/* Banner */}
            <div className="w-full h-64 rounded-3xl overflow-hidden relative shadow-lg">
                <img src={teacher.banner_url || 'https://img.freepik.com/free-vector/abstract-blue-background_698452-2979.jpg'} alt="Subject Banner" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-transparent flex items-center px-12 bg-[url('https://img.freepik.com/free-vector/abstract-blue-background_698452-2979.jpg')]">
                    <div className="text-white">
                        <p className="text-xl font-light opacity-90 mb-1">G. C. E. Ordinary Level</p>
                        <h1 className="text-6xl font-black tracking-tighter uppercase mb-2">{teacher.subject}</h1>
                        <p className="text-lg font-handwriting opacity-90">Unlock your potential with Sudesh Maths</p>
                    </div>
                </div>
            </div>

            {/* Content Split */}
            <div className="flex flex-col lg:flex-row gap-6">

                {/* Left: Teacher Profile */}
                <div className="w-full lg:w-80 space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
                        <img
                            src={teacher.image_url || 'https://via.placeholder.com/150'}
                            alt={teacher.name}
                            className="w-32 h-32 rounded-full border-4 border-blue-50 mx-auto mb-4 object-cover"
                        />
                        <h2 className="text-xl font-bold text-gray-900">{teacher.name}</h2>
                        <p className="text-[10px] uppercase font-black text-blue-600 tracking-widest mb-2">{teacher.subject}</p>
                        <p className="text-xs text-gray-500 mt-2 mb-4 px-2 italic">{teacher.qualifications}</p>

                        <div className="flex items-center justify-center gap-2 text-gray-600 bg-gray-50 py-2 rounded-lg text-sm font-medium">
                            <FiPhone size={16} />
                            <span>{teacher.phone}</span>
                        </div>
                    </div>
                </div>

                {/* Right: Classes Grid */}
                <div className="flex-1">
                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            onClick={() => setActiveTab('classes')}
                            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'classes'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Classes
                        </button>
                        <button
                            onClick={() => setActiveTab('packs')}
                            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'packs'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Study Packs
                        </button>
                    </div>

                    {/* Content Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activeTab === 'classes' && classes.map((cls) => (
                            <div key={cls.id} className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-shadow">
                                <div className="w-16 h-16 mx-auto bg-gray-50 rounded-2xl flex items-center justify-center mb-4 text-gray-400">
                                    <FiMonitor size={32} />
                                </div>
                                <h3 className="text-sm font-bold text-gray-900 text-center mb-4 h-10 overflow-hidden">{cls.name}</h3>

                                <div className="space-y-2 mb-6 text-center">
                                    <p className="text-xs text-blue-600 font-bold uppercase">{cls.type} • Grade {cls.grade}</p>
                                    <p className="text-xs font-bold text-gray-700">LKR {cls.fee}</p>
                                </div>

                                <button
                                    onClick={() => navigate(`/dashboard/courses/class/${cls.id}`)}
                                    className="w-full py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors uppercase shadow-lg shadow-blue-100"
                                >
                                    See Classes
                                </button>
                            </div>
                        ))}

                        {activeTab === 'packs' && study_packs.map((pack) => (
                            <div key={pack.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow relative">
                                <span className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg z-10">
                                    Study Pack
                                </span>

                                <div className="p-6">
                                    <div className="w-16 h-16 mx-auto bg-gray-50 rounded-2xl flex items-center justify-center mb-4 text-gray-300">
                                        <FiBookOpen size={32} />
                                    </div>
                                    <h3 className="text-sm font-bold text-gray-900 text-left mb-1 line-clamp-2 min-h-[40px]">{pack.title}</h3>
                                    <p className="text-xs text-gray-500 mb-4 line-clamp-2 italic">{pack.description}</p>

                                    <div className="flex items-center gap-4 mb-4 text-blue-600 text-xs font-bold">
                                        <div className="flex items-center gap-1">
                                            <FiMonitor /> <span>{pack.lessons_count} Lessons</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <FiFileText /> <span>{pack.tutes_count} Tutes</span>
                                        </div>
                                    </div>

                                    <div className="space-y-1 mb-4 border-t border-gray-50 pt-2 text-xs text-gray-500">
                                        <p><span className="font-bold">Fees:</span> LKR {pack.fee}</p>
                                        <p><span className="font-bold">Valid For:</span> {pack.validity_days} Days</p>
                                    </div>

                                    <button className="w-full py-2 bg-blue-50 text-blue-600 text-xs font-black rounded-lg hover:bg-blue-100 transition-colors uppercase">
                                        Purchase Pack
                                    </button>
                                </div>
                            </div>
                        ))}

                        {(activeTab === 'classes' && classes.length === 0) && (
                            <div className="col-span-full py-12 text-center text-gray-400 font-medium">No classes yet.</div>
                        )}
                        {(activeTab === 'packs' && study_packs.length === 0) && (
                            <div className="col-span-full py-12 text-center text-gray-400 font-medium">No study packs available.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
